import * as React from 'react';
import { Button } from '../../../components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface DayCellData {
  date: string;            // YYYY-MM-DD
  total_minutes: number;
  session_count: number;
  has_open_session: boolean;
}

interface Props {
  /** Month to display, as YYYY-MM. */
  month: string;
  onMonthChange: (yyyymm: string) => void;
  /** Sparse map of YYYY-MM-DD → cell data. Days not present render empty. */
  data: Record<string, DayCellData>;
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
  /**
   * IANA timezone for the "today" highlight + month-header label. Thread the
   * tenant/user timezone (e.g. identity.timezone). When omitted, falls back to
   * the browser's resolved timezone rather than forcing UTC.
   *
   * NB: the day-grid layout itself is computed with UTC calendar math, which is
   * timezone-independent (it only positions the 1st of the month in a weekday
   * column). Only "which calendar day is today" and the header month name are
   * timezone-sensitive — those honor `timeZone`.
   */
  timeZone?: string;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function pad(n: number) { return n < 10 ? `0${n}` : String(n); }
function ymd(y: number, m: number, d: number) { return `${y}-${pad(m)}-${pad(d)}`; }

/** Day number for `delta` months relative to (year, mo-1) at UTC. */
function neighborDay(year: number, mo: number, day: number) {
  const d = new Date(Date.UTC(year, mo - 1, day));
  return d.getUTCDate();
}

type Cell =
  | { kind: 'in'; date: string; day: number }
  | { kind: 'out'; day: number };

/** YYYY-MM-DD for "now" in the given IANA timezone (browser tz when omitted). */
function todayInTimeZone(timeZone?: string): string {
  try {
    // en-CA yields ISO-style YYYY-MM-DD; timeZone makes it tz-correct.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

export function MonthCalendar({ month, onMonthChange, data, selectedDate, onSelectDate, timeZone }: Props) {
  const [year, mo] = month.split('-').map(Number);

  const firstOfMonth = new Date(Date.UTC(year, mo - 1, 1));
  const startWeekday = firstOfMonth.getUTCDay();
  const daysInMonth = new Date(Date.UTC(year, mo, 0)).getUTCDate();
  const today = todayInTimeZone(timeZone);

  function prev() {
    const d = new Date(Date.UTC(year, mo - 2, 1));
    onMonthChange(`${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`);
  }
  function next() {
    const d = new Date(Date.UTC(year, mo, 1));
    onMonthChange(`${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`);
  }

  // Same calendar math as before — which in-month date falls in which slot is
  // unchanged. The only difference: leading/trailing blanks are now filled with
  // the actual prev/next-month day numbers (dimmed, non-interactive) for context.
  const cells: Cell[] = [];
  // Leading days from the previous month.
  for (let i = startWeekday; i > 0; i--) {
    cells.push({ kind: 'out', day: neighborDay(year, mo, 1 - i) });
  }
  // In-month days.
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ kind: 'in', date: ymd(year, mo, d), day: d });
  }
  // Trailing days from the next month.
  let trailing = 1;
  while (cells.length % 7 !== 0) {
    cells.push({ kind: 'out', day: neighborDay(year, mo, daysInMonth + trailing) });
    trailing++;
  }

  return (
    <div className="rounded-2xl border border-border/70 bg-card shadow-soft overflow-hidden">
      {/* Month nav header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
        <Button variant="ghost" size="icon" onClick={prev} aria-label="Previous month" className="rounded-full">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="text-[13.5px] font-semibold tracking-tight text-foreground">
          {/* firstOfMonth is a UTC-midnight Date; render its month name in UTC so
              the label matches the grid's UTC calendar math (not a tz-shift of it). */}
          {firstOfMonth.toLocaleString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' })}
        </div>
        <Button variant="ghost" size="icon" onClick={next} aria-label="Next month" className="rounded-full">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Weekday labels */}
      <div className="grid grid-cols-7 px-2 pt-2.5 pb-1">
        {WEEKDAYS.map((w, i) => {
          const isWeekend = i === 0 || i === 6;
          return (
            <div
              key={w}
              className={[
                'py-1 text-center text-[10px] font-semibold uppercase tracking-[0.12em]',
                isWeekend ? 'text-coral/70 dark:text-coral/60' : 'text-muted-foreground/70',
              ].join(' ')}
            >
              {w}
            </div>
          );
        })}
      </div>

      {/* Day tiles — gap-based grid of rounded tiles */}
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5 px-2 pb-2">
        {cells.map((cell, i) => {
          const col = i % 7;
          const isWeekend = col === 0 || col === 6;

          // Out-of-month context day: faint, non-interactive.
          if (cell.kind === 'out') {
            return (
              <div
                key={`out-${i}`}
                aria-hidden="true"
                className={[
                  'min-h-[44px] sm:min-h-[64px] rounded-xl px-1.5 py-1.5 select-none',
                  'text-[11px] sm:text-xs font-medium text-muted-foreground/40',
                  isWeekend ? 'bg-ink/[0.02] dark:bg-white/[0.03]' : '',
                ].join(' ')}
              >
                {cell.day}
              </div>
            );
          }

          const d = data[cell.date];
          const isSelected = cell.date === selectedDate;
          const isToday = cell.date === today;
          const hours = d ? (d.total_minutes / 60).toFixed(1) : null;

          return (
            <button
              type="button"
              key={cell.date}
              onClick={() => onSelectDate(cell.date)}
              aria-pressed={isSelected}
              aria-current={isToday ? 'date' : undefined}
              className={[
                'group relative min-h-[44px] sm:min-h-[64px] rounded-xl px-1.5 py-1.5',
                'flex flex-col items-stretch gap-1 text-left',
                'transition-colors duration-150 outline-none',
                'focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-1 focus-visible:ring-offset-card',
                // Base / weekend / hover surfaces.
                isSelected
                  ? 'bg-coral/15 ring-1 ring-inset ring-coral dark:bg-coral/20'
                  : isToday
                    ? 'ring-1 ring-inset ring-coral/60 hover:bg-coral/5'
                    : isWeekend
                      ? 'bg-ink/[0.02] dark:bg-white/[0.03] hover:bg-accent/10'
                      : 'hover:bg-accent/10',
              ].join(' ')}
            >
              {/* Day number */}
              <span
                className={[
                  'text-[12px] sm:text-[13px] leading-none font-semibold tabular-nums',
                  isToday
                    ? 'text-coral'
                    : isSelected
                      ? 'text-coral-700 dark:text-coral-200'
                      : 'text-foreground/90',
                ].join(' ')}
              >
                {cell.day}
              </span>

              {/* Hours badge + open-session dot */}
              {hours !== null && (
                <span className="mt-auto inline-flex items-center gap-1 self-start">
                  <span
                    className={[
                      'inline-flex items-center rounded-full px-1.5 py-0.5',
                      'text-[10px] sm:text-[10.5px] font-semibold tabular-nums leading-none',
                      isSelected
                        ? 'bg-coral/25 text-coral-700 dark:text-coral-100'
                        : 'bg-ink/[0.06] text-muted-foreground dark:bg-white/10',
                    ].join(' ')}
                  >
                    {hours}h
                  </span>
                  {/* An open session on TODAY just means "still on the clock";
                      on any past day it's a forgotten clock-out that blocks
                      every later clock-in, so it gets an alarm colour. */}
                  {d!.has_open_session && (
                    <span
                      title={isToday ? 'Currently clocked in' : 'Open session — never clocked out'}
                      className={[
                        'h-2 w-2 rounded-full ring-2 shadow-[0_0_0_1px_rgba(0,0,0,0.04)]',
                        isToday
                          ? 'bg-success ring-success/25'
                          : 'bg-coral ring-coral/30 dark:bg-coral-400 dark:ring-coral-400/30',
                      ].join(' ')}
                    />
                  )}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-3 border-t border-border/60 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-success ring-2 ring-success/25" />
          Clocked in now
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-[5px] ring-1 ring-inset ring-coral/60" />
          Today
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-[5px] bg-coral/15 ring-1 ring-inset ring-coral" />
          Selected
        </span>
      </div>
    </div>
  );
}
